import type { Components } from '@/api/generated/api';
import {
  fetchCheckoutSummary,
  fetchTenantCheckoutOptions,
  fetchTenantHasUsablePaymentMethod,
  fetchTenantInvoices,
  fetchTenantOffers,
  fetchTenantScheduledPlanChange,
  fetchUpcomingBillingAmount,
} from '@/api/resources/billing.api';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, type ComputedRef, type Ref, type MaybeRefOrGetter } from 'vue';
import { billingKeys } from './billing.keys';
import { useBillingState } from './useBillingState';

// Each reader subscribes only to its own resource and the status needed for access checks.
function useTenantBillingQuery<T>(
  key: (tenantId: number | null) => readonly unknown[],
  queryFn: () => Promise<T>,
  enabled: MaybeRefOrGetter<boolean>,
): { data: ComputedRef<T | undefined>; isLoading: Ref<boolean> } {
  const { currentTenant } = useCurrentTenant();
  const { canManageBilling } = useBillingState();
  const query = useQueryWithToast({
    queryKey: computed(() => key(currentTenant.value?.id ?? null)),
    queryFn,
    enabled: computed(() => currentTenant.value != null && canManageBilling.value && toValue(enabled)),
  });
  return { isLoading: query.isLoading, data: computed(() => (canManageBilling.value ? query.data.value : undefined)) };
}

export function useBillingInvoices(enabled: MaybeRefOrGetter<boolean> = true): {
  tenantInvoices: ComputedRef<Components.Schemas.BillingInvoiceDto[]>;
  isTenantInvoicesLoading: Ref<boolean>;
} {
  const query = useTenantBillingQuery(billingKeys.invoices, fetchTenantInvoices, enabled);
  return { tenantInvoices: computed(() => query.data.value ?? []), isTenantInvoicesLoading: query.isLoading };
}

export function useBillingOffers(enabled: MaybeRefOrGetter<boolean> = true): {
  tenantOffers: ComputedRef<Components.Schemas.BillingOfferDto[]>;
  isTenantOffersLoading: Ref<boolean>;
} {
  const query = useTenantBillingQuery(billingKeys.offers, fetchTenantOffers, enabled);
  return { tenantOffers: computed(() => query.data.value ?? []), isTenantOffersLoading: query.isLoading };
}

export function useBillingCheckoutOptions(enabled: MaybeRefOrGetter<boolean> = true): {
  checkoutOptions: ComputedRef<Components.Schemas.BillingCheckoutOptionsDtoData | undefined>;
} {
  const query = useTenantBillingQuery(billingKeys.checkoutOptions, fetchTenantCheckoutOptions, enabled);
  return { checkoutOptions: query.data };
}

export function useBillingScheduledPlanChange(enabled: MaybeRefOrGetter<boolean> = true): {
  scheduledPlanChangeEffectiveAt: ComputedRef<string | null>;
} {
  const query = useTenantBillingQuery(billingKeys.scheduledPlanChange, fetchTenantScheduledPlanChange, enabled);
  return { scheduledPlanChangeEffectiveAt: computed(() => query.data.value?.effectiveAt ?? null) };
}

export function useUpcomingBillingAmount(enabled: MaybeRefOrGetter<boolean> = true): {
  upcomingBillingAmount: ComputedRef<number | undefined>;
  upcomingBillingCurrency: ComputedRef<string | undefined>;
} {
  const query = useTenantBillingQuery(billingKeys.upcomingAmount, fetchUpcomingBillingAmount, enabled);
  return {
    upcomingBillingAmount: computed(() => query.data.value?.amountCents),
    upcomingBillingCurrency: computed(() => query.data.value?.currency ?? undefined),
  };
}

export function useBillingPaymentMethod(enabled: MaybeRefOrGetter<boolean> = true): {
  hasBillablePaymentMethod: ComputedRef<boolean | undefined>;
} {
  const query = useTenantBillingQuery(billingKeys.paymentMethod, fetchTenantHasUsablePaymentMethod, enabled);
  return { hasBillablePaymentMethod: query.data };
}

// A paid checkout does not change, so the return tracking and the receipt share one request; an unpaid one is read
// again, since its payment may have been confirmed in the meantime.
const getCheckoutSummaryStaleTime = ({
  state,
}: {
  state: { data?: Components.Schemas.CheckoutSummaryDto | null };
}): number => (state.data?.purchase ? Infinity : 0);

export function useCheckoutSummaryProducts(
  sessionId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
): { checkoutSummaryProducts: ComputedRef<Components.Schemas.ProductTypeDto[]> } {
  // The receipt belongs to the checkout session and must not wait for billing status to refresh.
  const query = useQueryWithToast({
    queryKey: computed(() => billingKeys.checkoutSummary(toValue(sessionId))),
    queryFn: () => {
      const id = toValue(sessionId);
      return id ? fetchCheckoutSummary(id) : null;
    },
    enabled: computed(() => toValue(enabled) && !!toValue(sessionId)),
    staleTime: getCheckoutSummaryStaleTime,
  });
  return { checkoutSummaryProducts: computed(() => query.data.value?.products ?? []) };
}

/** Reads a checkout summary through the cache entry the receipt uses. */
export function useCheckoutSummaryFetch(): (sessionId: string) => Promise<Components.Schemas.CheckoutSummaryDto> {
  const queryClient = useQueryClient();
  return (sessionId) =>
    queryClient.fetchQuery<Components.Schemas.CheckoutSummaryDto>({
      queryKey: billingKeys.checkoutSummary(sessionId),
      queryFn: () => fetchCheckoutSummary(sessionId),
      staleTime: getCheckoutSummaryStaleTime,
    });
}
